/**
 * Error Notification Service
 * Sends email alerts when booking/payment errors occur
 */

import config from '../config/environment';

const NOTIFICATION_EMAIL = 'jhartwell@belvoircastle.com';

/**
 * Send error notification email
 * @param {Object} errorDetails - Details about the error
 * @returns {Promise<boolean>} - Success status
 */
export const sendErrorNotification = async (errorDetails) => {
  // Destructure error details - variables are used in formatErrorEmail function
  // eslint-disable-next-line no-unused-vars
  const {
    errorType,
    errorMessage,
    bookingReference,
    orderId,
    paymentToken,
    transactionId,
    authCode,
    amount,
    customerDetails,
    selectedTickets,
    selectedDate,
    selectedTimeSlot,
    timestamp = new Date().toISOString(),
    stackTrace,
    endpoint,
    context
  } = errorDetails;

  try {

    // Prepare email content with improved subject lines
    const getSubject = () => {
      const endpointValue = endpoint || errorDetails.endpoint || 'Unknown Endpoint';
      const orderIdValue = orderId || errorDetails.orderId;
      const amountValue = amount || errorDetails.amount;
      
      // Check if this is a test endpoint/order - only flag if explicitly marked as test
      // or if orderId/bookingReference starts with "TEST_" (test page convention)
      // or if error message starts with "Test:" (test page convention)
      const errorMsg = errorMessage || errorDetails.errorMessage || '';
      const isTest = errorDetails.isTest === true ||
                     (orderIdValue && (orderIdValue.toUpperCase().startsWith('TEST_') || orderIdValue.toUpperCase() === 'TEST')) ||
                     (bookingReference && (bookingReference.toUpperCase().startsWith('TEST_') || bookingReference.toUpperCase() === 'TEST')) ||
                     (errorMsg && errorMsg.trim().toUpperCase().startsWith('TEST:'));
      const testPrefix = isTest ? 'TEST ' : '';
      
      if (errorType === 'PAYMENT_TAKEN_ORDER_FAILED' || errorType === 'PAYMENT_TAKEN_NO_BOOKING') {
        return `${testPrefix}🚨 URGENT: Payment Taken But Booking Failed - ${orderIdValue || 'No Order ID'} - £${amountValue?.toFixed(2) || '0.00'}`;
      }
      if (errorType === 'BOOKING_CREATED_NO_PAYMENT') {
        return `${testPrefix}🚨 CRITICAL: Booking Created Without Payment - ${bookingReference || orderIdValue || 'No ID'} - Expected: £${amountValue?.toFixed(2) || '0.00'}`;
      }
      if (errorType === 'PAYMENT_FAILED') {
        return `${testPrefix}⚠️ Payment Failed - ${orderIdValue || 'No Order ID'} - £${amountValue?.toFixed(2) || '0.00'}`;
      }
      if (errorType === 'MAINTENANCE_MODE_TRIGGERED') {
        return `${testPrefix}⚙️ Maintenance Mode Activated - Booking System Unavailable`;
      }
      if (errorType === 'API_ENDPOINT_FAILED' || errorType === 'VENPOS_API_ERROR') {
        return `${testPrefix}🔌 API Error: ${endpointValue} - ${orderIdValue || 'No Order ID'}`;
      }
      if (errorType === 'BASKET_CREATION_FAILED') {
        return `${testPrefix}🛒 Basket Creation Failed - ${orderIdValue || 'No Order ID'}`;
      }
      if (errorType === 'PAYMENT_INITIALIZATION_FAILED') {
        return `${testPrefix}💳 Payment Initialization Failed - ${orderIdValue || 'No Order ID'}`;
      }
      return `${testPrefix}❌ Booking Error: ${errorType} - ${orderIdValue || bookingReference || 'No ID'}`;
    };
    
    const subject = getSubject();

    const emailBody = formatErrorEmail(errorDetails);

    // Build API URL based on environment
    const apiUrl = `${config.apiEndpoint}/custom/v1/send-error-notification`;

    // Send via WordPress API endpoint
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        to: NOTIFICATION_EMAIL,
        subject: subject,
        body: emailBody,
        errorDetails: {
          errorType,
          orderId,
          bookingReference,
          transactionId,
          amount,
          timestamp,
          customerEmail: customerDetails?.email
        }
      })
    });

    if (response.ok) {

      return true;
    } else {

      return false;
    }
  } catch (error) {

    // Don't throw - notification failure shouldn't break user experience
    return false;
  }
};

/**
 * Format error details into readable email
 */
const formatErrorEmail = (errorDetails) => {
  const {
    errorType,
    errorMessage,
    bookingReference,
    orderId,
    paymentToken,
    transactionId,
    authCode,
    amount,
    customerDetails,
    selectedTickets,
    selectedDate,
    selectedTimeSlot,
    timestamp,
    stackTrace,
    endpoint,
    context
  } = errorDetails;

  // Determine severity and styling
  const isPaymentTaken = errorType === 'PAYMENT_TAKEN_ORDER_FAILED' || errorType === 'PAYMENT_TAKEN_NO_BOOKING';
  const isCritical = isPaymentTaken || errorType === 'BOOKING_CREATED_NO_PAYMENT';
  const isMaintenanceTriggered = errorType === 'MAINTENANCE_MODE_TRIGGERED';
  
  // Get error category and icon
  const getErrorCategory = () => {
    if (isPaymentTaken) return { label: 'CRITICAL - Payment Issue', color: '#dc3545', icon: '🚨' };
    if (errorType === 'BOOKING_CREATED_NO_PAYMENT') return { label: 'CRITICAL - Booking Issue', color: '#dc3545', icon: '🚨' };
    if (errorType === 'RATE_LIMIT_EXCEEDED' || errorType.includes('ATTACK') || errorType.includes('SECURITY')) return { label: 'SECURITY - Attack Detected', color: '#dc3545', icon: '🛡️' };
    if (errorType === 'PAYMENT_FAILED') return { label: 'Payment Error', color: '#ffc107', icon: '⚠️' };
    if (errorType.includes('API') || errorType.includes('ENDPOINT')) return { label: 'API Error', color: '#ff9800', icon: '🔌' };
    if (isMaintenanceTriggered) return { label: 'System Status', color: '#2196f3', icon: '⚙️' };
    return { label: 'Booking Error', color: '#6c757d', icon: '❌' };
  };
  
  const category = getErrorCategory();
  const formattedDate = new Date(timestamp).toLocaleString('en-GB', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  let emailHTML = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>
    @media only screen and (max-width: 600px) {
      .container { width: 100% !important; padding: 10px !important; }
      .header-title { font-size: 20px !important; }
      .info-grid { display: block !important; }
      .info-item { margin-bottom: 10px !important; }
    }
  </style>
</head>
  <body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; line-height: 1.6; color: #333; background: #f5f5f5; margin: 0; padding: 0;">
  <div style="max-width: 700px; margin: 0 auto; padding: 20px; background: #ffffff;">
    
    <!-- System Protection Alert Banner - ALWAYS SHOWN -->
    <div style="background-color: #dc3545 !important; background: linear-gradient(135deg, #8b0000 0%, #dc3545 50%, #c82333 100%) !important; color: #ffffff !important; padding: 30px 25px; border-radius: 12px; margin: -20px -20px 30px -20px; border: 3px solid #a71d2a; box-shadow: 0 8px 16px rgba(220, 53, 69, 0.4);">
      <div style="text-align: center; margin-bottom: 15px;">
        <div style="font-size: 48px; margin-bottom: 10px; color: #ffffff !important;">🛡️</div>
        <div style="font-size: 24px; font-weight: 900; text-transform: uppercase; letter-spacing: 2px; margin-bottom: 10px; text-shadow: 2px 2px 4px rgba(0,0,0,0.3); color: #ffffff !important;">
          SYSTEM PROTECTION ALERT
        </div>
        <div style="font-size: 18px; font-weight: 700; margin-bottom: 15px; text-shadow: 1px 1px 2px rgba(0,0,0,0.3); color: #ffffff !important;">
          CRITICAL DATA INTEGRITY VIOLATION DETECTED
        </div>
      </div>
      <div style="background-color: rgba(139, 0, 0, 0.3) !important; background: rgba(255, 255, 255, 0.15); padding: 20px; border-radius: 8px; border: 2px solid rgba(255, 255, 255, 0.3);">
        <div style="font-size: 16px; line-height: 1.8; color: #ffffff !important; text-align: center;">
          <strong style="color: #ffffff !important; font-size: 18px;">⚠️ WHY THIS ALERT WAS TRIGGERED:</strong><br><br>
          <span style="color: #ffffff !important;">The automated monitoring system has <strong style="color: #ffeb3b !important;">DETECTED A CRITICAL INCONSISTENCY</strong> in the booking process that could lead to:</span><br><br>
          <span style="color: #ffeb3b !important; font-weight: 700;">• DATA CORRUPTION</span><br>
          <span style="color: #ffeb3b !important; font-weight: 700;">• FINANCIAL DISCREPANCIES</span><br>
          <span style="color: #ffeb3b !important; font-weight: 700;">• CUSTOMER SERVICE FAILURES</span><br>
          <span style="color: #ffeb3b !important; font-weight: 700;">• SYSTEM INTEGRITY BREACHES</span><br><br>
          <span style="color: #ffffff !important;"><strong style="color: #ffffff !important;">The system has AUTOMATICALLY INTERVENED to prevent further errors and protect data integrity.</strong> Immediate manual review is required to resolve this issue and prevent potential data corruption.</span>
        </div>
      </div>
    </div>
    
    <!-- Header -->
    <div style="background-color: #101d40; background: linear-gradient(135deg, #101d40 0%, #2c3e50 100%); color: #ffffff !important; padding: 30px 25px; border-radius: 12px; margin-bottom: 25px; border: 2px solid #0d1529;">
      <div style="display: flex; align-items: center; gap: 15px; margin-bottom: 10px;">
        <div style="font-size: 36px; color: #ffffff !important;">${category.icon}</div>
        <div>
          <h1 class="header-title" style="margin: 0; font-size: 28px; font-weight: 700; color: #ffffff !important;">${isPaymentTaken ? '🚨 URGENT: Payment Taken But Booking Failed' : isMaintenanceTriggered ? '⚙️ Maintenance Mode Activated' : errorType === 'PAYMENT_FAILED' ? '⚠️ Payment Failed' : errorType === 'BOOKING_CREATED_NO_PAYMENT' ? '🚨 CRITICAL: Booking Created Without Payment' : errorType === 'RATE_LIMIT_EXCEEDED' || errorType.includes('ATTACK') || errorType.includes('SECURITY') ? '🛡️ SECURITY ALERT: Rate Limit Hit / Attack Blocked' : '❌ Booking System Error'}</h1>
          <div style="margin-top: 5px; font-size: 14px; color: #ffffff !important; opacity: 0.95;">${category.label}</div>
        </div>
      </div>
    </div>
    
    ${isPaymentTaken ? `
    <!-- Urgent Alert Banner -->
    <div style="background-color: #dc3545 !important; background: linear-gradient(135deg, #dc3545 0%, #c82333 50%, #a71d2a 100%) !important; color: #ffffff !important; padding: 30px; border-radius: 10px; margin-bottom: 25px; border: 4px solid #8b0000; box-shadow: 0 6px 12px rgba(220, 53, 69, 0.5);">
      <div style="font-size: 26px; font-weight: 900; margin-bottom: 15px; text-align: center; color: #ffffff !important; text-transform: uppercase; letter-spacing: 1px; text-shadow: 2px 2px 4px rgba(0,0,0,0.5);">
        🚨🚨🚨 URGENT ACTION REQUIRED 🚨🚨🚨
      </div>
      <div style="background-color: rgba(139, 0, 0, 0.3) !important; background: rgba(255, 255, 255, 0.2); padding: 20px; border-radius: 8px; border: 2px solid rgba(255, 255, 255, 0.3);">
        <div style="font-size: 18px; line-height: 1.8; text-align: center; color: #ffffff !important; margin-bottom: 15px;">
          <strong style="color: #ffeb3b !important; font-size: 20px;">CRITICAL DATA MISMATCH DETECTED:</strong>
        </div>
        <div style="font-size: 16px; line-height: 1.8; text-align: center; color: #ffffff !important;">
          <strong style="color: #ffffff !important;">⚠️ Customer's payment was successfully processed and charged, BUT the booking creation FAILED.</strong><br><br>
          <span style="color: #ffeb3b !important; font-weight: 700;">This creates a DANGEROUS DATA INCONSISTENCY:</span><br>
          <span style="color: #ffffff !important;">• Payment exists in payment system</span><br>
          <span style="color: #ffffff !important;">• Booking does NOT exist in booking system</span><br>
          <span style="color: #ffffff !important;">• Customer has been charged but has no booking</span><br><br>
          <span style="color: #ffffff !important;"><strong style="color: #ffffff !important;">IMMEDIATE MANUAL INTERVENTION REQUIRED</strong> to prevent data corruption and financial discrepancies. You must either complete the booking or process a full refund immediately.</span>
        </div>
      </div>
    </div>
    ` : ''}
    
    ${errorType === 'BOOKING_CREATED_NO_PAYMENT' ? `
    <!-- Critical Alert Banner -->
    <div style="background-color: #dc3545 !important; background: linear-gradient(135deg, #dc3545 0%, #c82333 50%, #a71d2a 100%) !important; color: #ffffff !important; padding: 30px; border-radius: 10px; margin-bottom: 25px; border: 4px solid #8b0000; box-shadow: 0 6px 12px rgba(220, 53, 69, 0.5);">
      <div style="font-size: 26px; font-weight: 900; margin-bottom: 15px; text-align: center; color: #ffffff !important; text-transform: uppercase; letter-spacing: 1px; text-shadow: 2px 2px 4px rgba(0,0,0,0.5);">
        🚨🚨🚨 CRITICAL DATA INTEGRITY BREACH 🚨🚨🚨
      </div>
      <div style="background-color: rgba(139, 0, 0, 0.3) !important; background: rgba(255, 255, 255, 0.2); padding: 20px; border-radius: 8px; border: 2px solid rgba(255, 255, 255, 0.3);">
        <div style="font-size: 18px; line-height: 1.8; text-align: center; color: #ffffff !important; margin-bottom: 15px;">
          <strong style="color: #ffeb3b !important; font-size: 20px;">SEVERE DATA INCONSISTENCY DETECTED:</strong>
        </div>
        <div style="font-size: 16px; line-height: 1.8; text-align: center; color: #ffffff !important;">
          <strong style="color: #ffffff !important;">⚠️ A booking was created in the system WITHOUT payment being processed, when payment was REQUIRED.</strong><br><br>
          <span style="color: #ffeb3b !important; font-weight: 700;">This creates a DANGEROUS DATA CORRUPTION RISK:</span><br>
          <span style="color: #ffffff !important;">• Booking exists in booking system</span><br>
          <span style="color: #ffffff !important;">• Payment does NOT exist in payment system</span><br>
          <span style="color: #ffffff !important;">• Customer has a booking but has NOT been charged</span><br>
          <span style="color: #ffffff !important;">• Expected payment amount: <strong style="color: #ffeb3b !important; font-size: 18px;">£${amount?.toFixed(2) || '0.00'}</strong></span><br><br>
          <span style="color: #ffffff !important;"><strong style="color: #ffffff !important;">IMMEDIATE INVESTIGATION REQUIRED</strong> to prevent data corruption, financial loss, and system integrity breaches. This could lead to significant revenue loss and customer service failures.</span>
        </div>
      </div>
    </div>
    ` : ''}
    
    ${errorType === 'RATE_LIMIT_EXCEEDED' || errorType.includes('ATTACK') || errorType.includes('SECURITY') ? `
    <!-- Security Alert Banner -->
    <div style="background-color: #dc3545 !important; background: linear-gradient(135deg, #8b0000 0%, #dc3545 50%, #c82333 100%) !important; color: #ffffff !important; padding: 30px; border-radius: 10px; margin-bottom: 25px; border: 4px solid #8b0000; box-shadow: 0 6px 12px rgba(220, 53, 69, 0.5);">
      <div style="font-size: 26px; font-weight: 900; margin-bottom: 15px; text-align: center; color: #ffffff !important; text-transform: uppercase; letter-spacing: 1px; text-shadow: 2px 2px 4px rgba(0,0,0,0.5);">
        🛡️🛡️🛡️ SECURITY ALERT: ATTACK BLOCKED 🛡️🛡️🛡️
      </div>
      <div style="background-color: rgba(139, 0, 0, 0.3) !important; background: rgba(255, 255, 255, 0.2); padding: 20px; border-radius: 8px; border: 2px solid rgba(255, 255, 255, 0.3);">
        <div style="font-size: 18px; line-height: 1.8; text-align: center; color: #ffffff !important; margin-bottom: 15px;">
          <strong style="color: #ffeb3b !important; font-size: 20px;">POTENTIAL ATTACK DETECTED:</strong>
        </div>
        <div style="font-size: 16px; line-height: 1.8; text-align: center; color: #ffffff !important;">
          <strong style="color: #ffffff !important;">⚠️ The automated security monitoring system has detected suspicious activity that may indicate an automated attack or brute force attempt.</strong><br><br>
          <span style="color: #ffeb3b !important; font-weight: 700;">Attack Details:</span><br>
          <span style="color: #ffffff !important;">• IP Address: <strong style="color: #ffeb3b !important;">${context?.ipAddress || 'Unknown'}</strong></span><br>
          <span style="color: #ffffff !important;">• Request Count: <strong style="color: #ffeb3b !important;">${context?.requestCount || 'Unknown'}</strong> requests</span><br>
          <span style="color: #ffffff !important;">• Time Window: <strong style="color: #ffeb3b !important;">${context?.timeWindow || 'Unknown'}</strong></span><br>
          <span style="color: #ffffff !important;">• Status: <strong style="color: #ffeb3b !important;">BLOCKED</strong></span><br><br>
          <span style="color: #ffffff !important;"><strong style="color: #ffffff !important;">The system has automatically blocked this IP address to protect against potential data breaches, system overload, and unauthorized access attempts.</strong> Review the security logs and consider additional protective measures if this pattern continues.</span>
        </div>
      </div>
    </div>
    ` : ''}
    
    ${isMaintenanceTriggered ? `
    <!-- Maintenance Mode Banner -->
    <div style="background-color: #ffc107 !important; background: linear-gradient(135deg, #ffc107 0%, #ff9800 50%, #f57c00 100%) !important; color: #333 !important; padding: 30px; border-radius: 10px; margin-bottom: 25px; border: 4px solid #e65100; box-shadow: 0 6px 12px rgba(255, 193, 7, 0.5);">
      <div style="font-size: 26px; font-weight: 900; margin-bottom: 15px; text-align: center; color: #333 !important; text-transform: uppercase; letter-spacing: 1px; text-shadow: 1px 1px 2px rgba(255,255,255,0.5);">
        ⚙️ AUTOMATIC PROTECTION MODE ACTIVATED ⚙️
      </div>
      <div style="background-color: rgba(255, 255, 255, 0.9) !important; background: rgba(255, 255, 255, 0.7); padding: 20px; border-radius: 8px; border: 2px solid rgba(0, 0, 0, 0.1);">
        <div style="font-size: 18px; line-height: 1.8; text-align: center; color: #333 !important; margin-bottom: 15px;">
          <strong style="color: #e65100 !important; font-size: 20px;">SYSTEM PROTECTION MEASURE TRIGGERED:</strong>
        </div>
        <div style="font-size: 16px; line-height: 1.8; text-align: center; color: #333 !important;">
          <strong style="color: #333 !important;">🛡️ The automated monitoring system has DETECTED MULTIPLE CRITICAL ERRORS and has AUTOMATICALLY placed the booking system into maintenance mode to PREVENT DATA CORRUPTION.</strong><br><br>
          <span style="color: #e65100 !important; font-weight: 700;">This protective measure prevents:</span><br>
          <span style="color: #333 !important;">• Further data integrity breaches</span><br>
          <span style="color: #333 !important;">• Additional financial discrepancies</span><br>
          <span style="color: #333 !important;">• Customer service failures</span><br>
          <span style="color: #333 !important;">• System-wide data corruption</span><br><br>
          <span style="color: #333 !important;"><strong style="color: #333 !important;">Users cannot make new bookings until the underlying issues are resolved.</strong> This is a protective measure to safeguard data integrity and prevent further errors.</span>
        </div>
      </div>
    </div>
    ` : ''}
    
    <!-- Quick Summary Card -->
    <div style="background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%); padding: 20px; border-radius: 10px; margin-bottom: 25px; border: 2px solid #dee2e6;">
      <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 15px;">
        ${orderId ? `
        <div style="background: white; padding: 15px; border-radius: 8px; border-left: 4px solid #007bff;">
          <div style="font-size: 12px; color: #6c757d; text-transform: uppercase; font-weight: 600; margin-bottom: 5px;">Order ID</div>
          <div style="font-size: 18px; font-weight: 700; color: #007bff; word-break: break-all;">${orderId}</div>
        </div>
        ` : ''}
        ${bookingReference ? `
        <div style="background: white; padding: 15px; border-radius: 8px; border-left: 4px solid #28a745;">
          <div style="font-size: 12px; color: #6c757d; text-transform: uppercase; font-weight: 600; margin-bottom: 5px;">Booking Reference</div>
          <div style="font-size: 18px; font-weight: 700; color: #28a745;">${bookingReference}</div>
        </div>
        ` : ''}
        ${amount ? `
        <div style="background: white; padding: 15px; border-radius: 8px; border-left: 4px solid ${isPaymentTaken ? '#dc3545' : '#ffc107'};">
          <div style="font-size: 12px; color: #6c757d; text-transform: uppercase; font-weight: 600; margin-bottom: 5px;">Amount</div>
          <div style="font-size: 24px; font-weight: 700; color: ${isPaymentTaken ? '#dc3545' : '#ffc107'};">£${amount.toFixed(2)}</div>
        </div>
        ` : ''}
        <div style="background: white; padding: 15px; border-radius: 8px; border-left: 4px solid #6c757d;">
          <div style="font-size: 12px; color: #6c757d; text-transform: uppercase; font-weight: 600; margin-bottom: 5px;">Time</div>
          <div style="font-size: 14px; font-weight: 600; color: #333;">${formattedDate}</div>
        </div>
      </div>
    </div>
    
    <!-- Error Details -->
    <div style="background: #ffffff; border: 2px solid #e9ecef; border-radius: 10px; padding: 20px; margin-bottom: 20px;">
      <h2 style="margin: 0 0 15px 0; font-size: 20px; color: #101d40; border-bottom: 2px solid #e9ecef; padding-bottom: 10px; display: flex; align-items: center; gap: 10px;">
        <span style="font-size: 24px;">📋</span> Error Details
      </h2>
      <div class="info-grid" style="display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 15px;">
        <div class="info-item" style="padding: 12px; background: #f8f9fa; border-radius: 6px;">
          <div style="font-size: 12px; color: #6c757d; text-transform: uppercase; font-weight: 600; margin-bottom: 5px;">Error Type</div>
          <div style="font-size: 16px; font-weight: 600; color: #333;">${errorType}</div>
        </div>
        ${endpoint ? `
        <div class="info-item" style="padding: 12px; background: #f8f9fa; border-radius: 6px;">
          <div style="font-size: 12px; color: #6c757d; text-transform: uppercase; font-weight: 600; margin-bottom: 5px;">Endpoint</div>
          <div style="font-size: 14px; font-weight: 600; color: #333; word-break: break-all;">${endpoint}</div>
        </div>
        ` : ''}
      </div>
      <div style="margin-top: 15px; padding: 15px; background: #fff3cd; border-left: 4px solid #ffc107; border-radius: 6px;">
        <div style="font-size: 12px; color: #856404; text-transform: uppercase; font-weight: 600; margin-bottom: 5px;">Error Message</div>
        <div style="font-size: 15px; color: #856404; font-weight: 500;">${errorMessage || 'No error message provided'}</div>
      </div>
      ${!isPaymentTaken && errorType !== 'BOOKING_CREATED_NO_PAYMENT' && !isMaintenanceTriggered && errorType !== 'RATE_LIMIT_EXCEEDED' && !errorType.includes('ATTACK') && !errorType.includes('SECURITY') ? `
      <div style="margin-top: 15px; padding: 15px; background: #e7f3ff; border-left: 4px solid #007bff; border-radius: 6px;">
        <div style="font-size: 12px; color: #004085; text-transform: uppercase; font-weight: 600; margin-bottom: 8px;">🛡️ Why This Was Detected:</div>
        <div style="font-size: 14px; color: #004085; line-height: 1.6;">
          The automated monitoring system detected this error to <strong style="color: #004085;">prevent potential data corruption, system failures, and customer service issues</strong>. Early detection allows for prompt resolution before the issue escalates and causes data integrity problems.
        </div>
      </div>
      ` : ''}
    </div>
    
    <!-- Payment Information -->
    ${amount || transactionId || authCode || paymentToken ? `
    <div style="background: #ffffff; border: 2px solid #e9ecef; border-radius: 10px; padding: 20px; margin-bottom: 20px;">
      <h2 style="margin: 0 0 15px 0; font-size: 20px; color: #101d40; border-bottom: 2px solid #e9ecef; padding-bottom: 10px; display: flex; align-items: center; gap: 10px;">
        <span style="font-size: 24px;">💳</span> Payment Information
      </h2>
      <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 15px;">
    ${amount ? `
        <div style="padding: 15px; background: ${isPaymentTaken ? '#fff3cd' : '#e7f3ff'}; border-radius: 8px; border-left: 4px solid ${isPaymentTaken ? '#ffc107' : '#007bff'};">
          <div style="font-size: 12px; color: #6c757d; text-transform: uppercase; font-weight: 600; margin-bottom: 8px;">Amount</div>
          <div style="font-size: 28px; font-weight: 700; color: ${isPaymentTaken ? '#856404' : '#007bff'};">£${amount.toFixed(2)}</div>
          ${isPaymentTaken ? `
          <div style="margin-top: 8px; padding: 8px; background: #dc3545; color: white; border-radius: 4px; font-size: 12px; font-weight: 600; text-align: center;">
            ⚠️ PAYMENT PROCESSED
          </div>
          ` : ''}
        </div>
        ` : ''}
        ${transactionId ? `
        <div style="padding: 12px; background: #f8f9fa; border-radius: 6px;">
          <div style="font-size: 12px; color: #6c757d; text-transform: uppercase; font-weight: 600; margin-bottom: 5px;">Transaction ID</div>
          <div style="font-size: 14px; font-weight: 600; color: #333; word-break: break-all; font-family: monospace;">${transactionId}</div>
        </div>
        ` : ''}
        ${authCode ? `
        <div style="padding: 12px; background: #f8f9fa; border-radius: 6px;">
          <div style="font-size: 12px; color: #6c757d; text-transform: uppercase; font-weight: 600; margin-bottom: 5px;">Auth Code</div>
          <div style="font-size: 16px; font-weight: 700; color: #28a745; font-family: monospace;">${authCode}</div>
        </div>
        ` : ''}
        ${paymentToken ? `
        <div style="padding: 12px; background: #f8f9fa; border-radius: 6px;">
          <div style="font-size: 12px; color: #6c757d; text-transform: uppercase; font-weight: 600; margin-bottom: 5px;">Payment Token</div>
          <div style="font-size: 12px; font-weight: 600; color: #333; word-break: break-all; font-family: monospace;">${paymentToken.substring(0, 50)}${paymentToken.length > 50 ? '...' : ''}</div>
        </div>
        ` : ''}
      </div>
    </div>
    ` : ''}
    
    <!-- Customer Information -->
    ${customerDetails ? `
    <div style="background: #ffffff; border: 2px solid #e9ecef; border-radius: 10px; padding: 20px; margin-bottom: 20px;">
      <h2 style="margin: 0 0 15px 0; font-size: 20px; color: #101d40; border-bottom: 2px solid #e9ecef; padding-bottom: 10px; display: flex; align-items: center; gap: 10px;">
        <span style="font-size: 24px;">👤</span> Customer Information
      </h2>
      <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 15px;">
        <div style="padding: 15px; background: #f8f9fa; border-radius: 8px;">
          <div style="font-size: 12px; color: #6c757d; text-transform: uppercase; font-weight: 600; margin-bottom: 5px;">Name</div>
          <div style="font-size: 16px; font-weight: 600; color: #333;">${customerDetails.firstName} ${customerDetails.lastName}</div>
        </div>
        <div style="padding: 15px; background: #e7f3ff; border-radius: 8px; border-left: 4px solid #007bff;">
          <div style="font-size: 12px; color: #6c757d; text-transform: uppercase; font-weight: 600; margin-bottom: 5px;">Email</div>
          <div style="font-size: 16px; font-weight: 600; color: #007bff;">
            <a href="mailto:${customerDetails.email}" style="color: #007bff; text-decoration: none;">${customerDetails.email}</a>
          </div>
        </div>
        ${customerDetails.phone ? `
        <div style="padding: 15px; background: #f8f9fa; border-radius: 8px;">
          <div style="font-size: 12px; color: #6c757d; text-transform: uppercase; font-weight: 600; margin-bottom: 5px;">Phone</div>
          <div style="font-size: 16px; font-weight: 600; color: #333;">
            <a href="tel:${customerDetails.phone}" style="color: #333; text-decoration: none;">${customerDetails.phone}</a>
          </div>
        </div>
        ` : ''}
      </div>
      ${customerDetails.address ? `
      <div style="margin-top: 15px; padding: 15px; background: #f8f9fa; border-radius: 8px;">
        <div style="font-size: 12px; color: #6c757d; text-transform: uppercase; font-weight: 600; margin-bottom: 8px;">Address</div>
        <div style="font-size: 14px; color: #333; line-height: 1.6;">
          ${customerDetails.address.line1 || ''}<br>
          ${customerDetails.address.line2 ? customerDetails.address.line2 + '<br>' : ''}
          ${customerDetails.address.city || ''} ${customerDetails.address.postcode || ''}<br>
          ${customerDetails.address.country || ''}
        </div>
      </div>
      ` : ''}
    </div>
    ` : ''}
    
    <!-- Booking Details -->
    ${selectedTickets && selectedTickets.length > 0 ? `
    <div style="background: #ffffff; border: 2px solid #e9ecef; border-radius: 10px; padding: 20px; margin-bottom: 20px;">
      <h2 style="margin: 0 0 15px 0; font-size: 20px; color: #101d40; border-bottom: 2px solid #e9ecef; padding-bottom: 10px; display: flex; align-items: center; gap: 10px;">
        <span style="font-size: 24px;">🎫</span> Booking Details
      </h2>
      <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 15px; margin-bottom: 15px;">
        ${selectedDate ? `
        <div style="padding: 12px; background: #f8f9fa; border-radius: 6px;">
          <div style="font-size: 12px; color: #6c757d; text-transform: uppercase; font-weight: 600; margin-bottom: 5px;">Visit Date</div>
          <div style="font-size: 16px; font-weight: 600; color: #333;">${new Date(selectedDate).toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
        </div>
        ` : ''}
        ${selectedTimeSlot?.time ? `
        <div style="padding: 12px; background: #f8f9fa; border-radius: 6px;">
          <div style="font-size: 12px; color: #6c757d; text-transform: uppercase; font-weight: 600; margin-bottom: 5px;">Time Slot</div>
          <div style="font-size: 16px; font-weight: 600; color: #333;">${selectedTimeSlot.time}</div>
        </div>
        ` : ''}
      </div>
      <div style="background: #f8f9fa; padding: 15px; border-radius: 8px;">
        <div style="font-size: 14px; font-weight: 600; color: #333; margin-bottom: 10px;">Tickets Selected:</div>
        <table style="width: 100%; border-collapse: collapse;">
          <thead>
            <tr style="background: #e9ecef;">
              <th style="padding: 10px; text-align: left; font-size: 12px; color: #6c757d; text-transform: uppercase; font-weight: 600;">Ticket</th>
              <th style="padding: 10px; text-align: center; font-size: 12px; color: #6c757d; text-transform: uppercase; font-weight: 600;">Quantity</th>
              <th style="padding: 10px; text-align: right; font-size: 12px; color: #6c757d; text-transform: uppercase; font-weight: 600;">Price Each</th>
              <th style="padding: 10px; text-align: right; font-size: 12px; color: #6c757d; text-transform: uppercase; font-weight: 600;">Total</th>
            </tr>
          </thead>
          <tbody>
        ${selectedTickets.filter(t => t.quantity > 0).map(ticket => `
            <tr style="border-bottom: 1px solid #dee2e6;">
              <td style="padding: 10px; font-weight: 600; color: #333;">${ticket.name}</td>
              <td style="padding: 10px; text-align: center; color: #333;">${ticket.quantity}</td>
              <td style="padding: 10px; text-align: right; color: #333;">£${(ticket.price || 0).toFixed(2)}</td>
              <td style="padding: 10px; text-align: right; font-weight: 600; color: #333;">£${((ticket.price || 0) * ticket.quantity).toFixed(2)}</td>
            </tr>
        `).join('')}
          </tbody>
        </table>
      </div>
    </div>
    ` : ''}
    
    <!-- Technical Details (Collapsible) -->
    ${stackTrace || context ? `
    <div style="background: #ffffff; border: 2px solid #e9ecef; border-radius: 10px; padding: 20px; margin-bottom: 20px;">
      <h2 style="margin: 0 0 15px 0; font-size: 20px; color: #101d40; border-bottom: 2px solid #e9ecef; padding-bottom: 10px; display: flex; align-items: center; gap: 10px;">
        <span style="font-size: 24px;">🔧</span> Technical Details
      </h2>
      ${context && Object.keys(context).length > 0 ? `
      <div style="margin-bottom: 15px; padding: 15px; background: #f8f9fa; border-radius: 8px;">
        <div style="font-size: 12px; color: #6c757d; text-transform: uppercase; font-weight: 600; margin-bottom: 8px;">Context</div>
        <pre style="margin: 0; font-size: 13px; color: #333; font-family: 'Courier New', monospace; white-space: pre-wrap; word-break: break-all;">${JSON.stringify(context, null, 2)}</pre>
      </div>
      ` : ''}
    ${stackTrace ? `
      <div style="padding: 15px; background: #2d2d2d; border-radius: 8px; overflow-x: auto;">
        <div style="font-size: 12px; color: #999; text-transform: uppercase; font-weight: 600; margin-bottom: 8px;">Stack Trace</div>
        <pre style="margin: 0; font-size: 12px; color: #f8f8f2; font-family: 'Courier New', monospace; white-space: pre-wrap; word-break: break-all; line-height: 1.5;">${stackTrace}</pre>
      </div>
      ` : ''}
    </div>
    ` : ''}
    
    <!-- Action Steps -->
    <div style="background: ${isPaymentTaken ? 'linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%)' : isCritical ? 'linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%)' : 'linear-gradient(135deg, #d1ecf1 0%, #bee5eb 100%)'}; border: 3px solid ${isPaymentTaken ? '#ffc107' : isCritical ? '#dc3545' : '#17a2b8'}; border-radius: 10px; padding: 25px; margin-bottom: 20px;">
      <h2 style="margin: 0 0 15px 0; font-size: 22px; color: ${isPaymentTaken ? '#856404' : isCritical ? '#721c24' : '#0c5460'}; display: flex; align-items: center; gap: 10px;">
        <span style="font-size: 28px;">📋</span> Required Actions
      </h2>
      ${isPaymentTaken ? `
        <div style="background: rgba(220, 53, 69, 0.1); padding: 20px; border-radius: 8px; border-left: 5px solid #dc3545; margin-bottom: 15px;">
          <div style="font-size: 18px; font-weight: 700; color: #dc3545; margin-bottom: 15px; text-transform: uppercase;">
            🚨 CRITICAL ACTIONS REQUIRED TO PREVENT DATA CORRUPTION:
          </div>
          <ol style="margin: 0; padding-left: 25px; line-height: 2.2; color: #856404; font-size: 15px;">
            <li style="margin-bottom: 15px;">
              <strong style="color: #dc3545; font-size: 16px;">STEP 1 - IMMEDIATE CUSTOMER CONTACT:</strong><br>
              <span style="color: #333;">Contact customer${customerDetails?.email ? ` at <a href="mailto:${customerDetails.email}" style="color: #007bff; font-weight: 600; text-decoration: none;">${customerDetails.email}</a>` : ''}${customerDetails?.phone ? `${customerDetails?.email ? ' or ' : ' at '}<a href="tel:${customerDetails.phone}" style="color: #007bff; font-weight: 600; text-decoration: none;">${customerDetails.phone}</a>` : customerDetails?.email ? '' : ' (contact details not available)'} to explain the situation and prevent customer service issues.</span>
            </li>
            <li style="margin-bottom: 15px;">
              <strong style="color: #dc3545; font-size: 16px;">STEP 2 - RESTORE DATA INTEGRITY:</strong><br>
              <span style="color: #333;">Manually create the booking in Venpos to match the payment that was processed. This restores data consistency and prevents corruption.</span><br>
              <span style="background: white; padding: 8px 12px; border-radius: 4px; font-family: monospace; font-size: 13px; display: inline-block; margin-top: 8px; border: 2px solid #dc3545; font-weight: 700;">Order ID: ${orderId || 'N/A'}</span>
            </li>
            <li style="margin-bottom: 15px;">
              <strong style="color: #dc3545; font-size: 16px;">STEP 3 - COMPLETE THE TRANSACTION:</strong><br>
              <span style="color: #333;">Send booking confirmation email to customer with booking reference to complete the customer experience and maintain data integrity.</span>
            </li>
            <li style="margin-bottom: 0;">
              <strong style="color: #dc3545; font-size: 16px;">STEP 4 - ALTERNATIVE IF BOOKING CANNOT BE CREATED:</strong><br>
              <span style="color: #333;">If the booking cannot be manually created, you MUST process a full refund immediately to prevent financial discrepancies and data corruption.</span><br>
              <span style="background: white; padding: 8px 12px; border-radius: 4px; font-family: monospace; font-size: 13px; display: inline-block; margin-top: 8px; border: 2px solid #dc3545; font-weight: 700;">Transaction ID: ${transactionId || authCode || 'N/A'}</span>
            </li>
          </ol>
        </div>
      ` : errorType === 'BOOKING_CREATED_NO_PAYMENT' ? `
        <div style="background: rgba(220, 53, 69, 0.1); padding: 20px; border-radius: 8px; border-left: 5px solid #dc3545; margin-bottom: 15px;">
          <div style="font-size: 18px; font-weight: 700; color: #dc3545; margin-bottom: 15px; text-transform: uppercase;">
            🚨 CRITICAL ACTIONS REQUIRED TO PREVENT DATA CORRUPTION:
          </div>
          <ol style="margin: 0; padding-left: 25px; line-height: 2.2; color: #721c24; font-size: 15px;">
            <li style="margin-bottom: 15px;">
              <strong style="color: #dc3545; font-size: 16px;">STEP 1 - IMMEDIATE INVESTIGATION:</strong><br>
              <span style="color: #333;">Investigate why the booking was created without payment. This is a critical data integrity breach that could lead to financial loss and system corruption.</span>
            </li>
            <li style="margin-bottom: 15px;">
              <strong style="color: #dc3545; font-size: 16px;">STEP 2 - VERIFY BOOKING STATUS:</strong><br>
              <span style="color: #333;">Check Venpos system for booking reference to confirm the booking exists and assess the extent of the data inconsistency.</span><br>
              <span style="background: white; padding: 8px 12px; border-radius: 4px; font-family: monospace; font-size: 13px; display: inline-block; margin-top: 8px; border: 2px solid #dc3545; font-weight: 700;">Booking Reference: ${bookingReference || 'N/A'}</span>
            </li>
            <li style="margin-bottom: 15px;">
              <strong style="color: #dc3545; font-size: 16px;">STEP 3 - RESOLVE PAYMENT ISSUE:</strong><br>
              <span style="color: #333;">Contact customer immediately to collect payment (Expected: <strong style="color: #dc3545;">£${amount?.toFixed(2) || '0.00'}</strong>) or cancel the booking if payment cannot be collected. This prevents financial discrepancies and data corruption.</span>
            </li>
            <li style="margin-bottom: 0;">
              <strong style="color: #dc3545; font-size: 16px;">STEP 4 - IDENTIFY ROOT CAUSE:</strong><br>
              <span style="color: #333;">Review system logs and investigate the root cause to prevent this data integrity breach from recurring. This is critical to prevent future data corruption.</span>
            </li>
          </ol>
        </div>
      ` : isMaintenanceTriggered ? `
        <div style="background: rgba(255, 193, 7, 0.1); padding: 20px; border-radius: 8px; border-left: 5px solid #ffc107; margin-bottom: 15px;">
          <div style="font-size: 18px; font-weight: 700; color: #856404; margin-bottom: 15px; text-transform: uppercase;">
            ⚙️ SYSTEM PROTECTION ACTIONS REQUIRED:
          </div>
          <div style="color: #0c5460; font-size: 15px; line-height: 1.8; margin-bottom: 15px;">
            <p style="margin: 0 0 15px 0; font-weight: 700; color: #856404;"><strong>🛡️ Maintenance mode has been AUTOMATICALLY ENABLED by the system's protection mechanism to prevent further data corruption.</strong> The booking system is now unavailable to users to protect data integrity.</p>
          </div>
          <div style="color: #0c5460; font-size: 15px; line-height: 1.8;">
            <p style="margin: 0 0 15px 0; font-weight: 700;"><strong>Required Actions to Restore System:</strong></p>
            <ol style="margin: 0; padding-left: 25px; line-height: 2.2;">
              <li style="margin-bottom: 10px;"><strong style="color: #856404;">Investigate the root cause</strong> of the critical errors that triggered this protection mode</li>
              <li style="margin-bottom: 10px;"><strong style="color: #856404;">Fix all underlying issues</strong> to prevent data corruption and system failures</li>
              <li style="margin-bottom: 10px;"><strong style="color: #856404;">Test the booking system thoroughly</strong> to ensure data integrity is restored</li>
              <li style="margin-bottom: 0;"><strong style="color: #856404;">Disable maintenance mode</strong> via admin panel or API once all issues are resolved</li>
            </ol>
          </div>
        </div>
      ` : `
        <div style="background: rgba(23, 162, 184, 0.1); padding: 20px; border-radius: 8px; border-left: 5px solid #17a2b8; margin-bottom: 15px;">
          <div style="font-size: 18px; font-weight: 700; color: #0c5460; margin-bottom: 15px; text-transform: uppercase;">
            ⚠️ MONITORING AND PREVENTION ACTIONS:
          </div>
          <div style="color: #0c5460; font-size: 15px; line-height: 1.8;">
            <p style="margin: 0 0 15px 0; font-weight: 600;"><strong>🛡️ System Protection Status:</strong> The automated monitoring system detected this error to prevent potential data corruption and system failures.</p>
            <p style="margin: 0 0 15px 0;"><strong>Customer Impact:</strong> Customer can retry booking. No payment was taken, so no financial data corruption risk at this time.</p>
            <p style="margin: 0; font-weight: 600; color: #0c5460;"><strong>⚠️ Required Action:</strong> Monitor for patterns. If errors persist or escalate, investigate system stability immediately to prevent data integrity issues and potential corruption.</p>
          </div>
        </div>
      `}
    </div>
    
    <!-- Footer -->
    <div style="background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%); padding: 25px; border-radius: 10px; margin-top: 25px; text-align: center; border-top: 4px solid #dc3545; border: 2px solid #dee2e6;">
      <div style="font-size: 13px; color: #6c757d; line-height: 1.8;">
        <p style="margin: 0 0 10px 0; font-size: 16px; font-weight: 700; color: #101d40;"><strong>🛡️ Belvoir Castle Booking System - Automated Protection Alert</strong></p>
        <p style="margin: 0 0 10px 0; font-size: 14px; color: #dc3545; font-weight: 600;">
          <strong>⚠️ This alert was automatically generated by the system's data integrity monitoring to prevent errors and data corruption.</strong>
        </p>
        <p style="margin: 0 0 10px 0; font-size: 13px; color: #666;">
          The system detected a critical inconsistency and has intervened to protect data integrity. Immediate investigation and resolution is required to prevent potential data corruption, financial discrepancies, and customer service failures.
        </p>
        <p style="margin: 15px 0 0 0; padding-top: 15px; border-top: 1px solid #dee2e6; font-size: 11px; color: #999;">
          Error ID: ${orderId || bookingReference || Date.now()} | Detected: ${formattedDate}
        </p>
      </div>
    </div>
    
  </div>
</body>
</html>
`;

  return emailHTML;
};

/**
 * Log error details to console for debugging
 */
export const logErrorDetails = (errorDetails) => {
  console.group('🚨 BOOKING ERROR - DETAILED LOG');


  if (errorDetails.bookingReference) 
  if (errorDetails.orderId) 
  if (errorDetails.transactionId) 
  if (errorDetails.paymentToken) 
  if (errorDetails.amount) 
  
  if (errorDetails.customerDetails) {
    console.group('Customer Details:');


    console.groupEnd();
  }
  
  if (errorDetails.selectedTickets) {
    console.group('Tickets:');
    errorDetails.selectedTickets.filter(t => t.quantity > 0).forEach(ticket => {

    });
    console.groupEnd();
  }
  
  if (errorDetails.stackTrace) {

  }
  
  console.groupEnd();
};

